import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { bookingsService } from '../../services/supabaseService';
import { Calendar } from 'lucide-react';

const DashboardBookingsPage = () => {
  const { user, userProfile } = useAuth();
  const isProvider = userProfile?.role === 'service_provider';
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    const load = isProvider
      ? () => bookingsService.listForProvider(user.id)
      : () => bookingsService.listForUser(user.id);
    load().then((data) => {
      setBookings(Array.isArray(data) ? data : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user?.id, isProvider]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-pink border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-heading font-bold text-2xl text-white mb-6">
        {isProvider ? 'Bookings' : 'My Bookings'}
      </h1>
      {bookings.length === 0 ? (
        <div className="p-12 rounded-2xl bg-slate-800/50 border border-white/10 text-center text-slate-400">
          <Calendar className="h-12 w-12 mx-auto mb-4 text-slate-500" />
          <p>
            {isProvider
              ? 'No bookings yet. Users can book you from your profile.'
              : 'No bookings yet. Book a friend from their profile.'}
          </p>
          {!isProvider && (
            <Link to="/dashboard/find" className="text-brand-pink font-medium mt-2 inline-block hover:underline">
              Find a Friend
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((b) => {
            const other = isProvider
              ? b.user_profiles
              : b.user_profiles;
            const name = other?.display_name || other?.username || 'Friend';
            const slot = new Date(b.slot);
            return (
              <div
                key={b.id}
                className="p-4 rounded-2xl bg-slate-800/50 border border-white/10 flex items-center justify-between flex-wrap gap-2"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-700 overflow-hidden">
                    <img
                      src={other?.avatar_url || '/default-avatar.svg'}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{name}</p>
                    <p className="text-sm text-slate-400">
                      {slot.toLocaleString(undefined, {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {b.amount_paise != null && (
                    <span className="text-brand-pink font-semibold">
                      ₹{(b.amount_paise / 100).toLocaleString()}
                    </span>
                  )}
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      b.status === 'confirmed'
                        ? 'bg-green-500/20 text-green-300'
                        : b.status === 'cancelled'
                        ? 'bg-slate-600 text-slate-400'
                        : 'bg-amber-500/20 text-amber-300'
                    }`}
                  >
                    {b.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DashboardBookingsPage;
